/**
 * ProjectsListPage — every shared workspace this person can open.
 *
 * Split into "Yours" and "Shared with you" rather than one flat list, because
 * the two carry different obligations: one you are responsible for, the other
 * someone invited you into. A single list buries that distinction under a sort
 * order.
 *
 * Closed (archived) projects are hidden behind a toggle rather than deleted
 * from view — a closed project still holds the group's work, and its creator
 * has to be able to find and reopen it.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { activeLocale } from '@/utils/locale';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '@/components/layout/Sidebar';
import { useAuth } from '@/hooks/useAuth';
import { useTranslator } from '../hooks/useTranslator';
import { useTenantPath } from '@/routing';
import projectService from '../services/projectService';
import type { ProjectSummary } from '../types';
import { ProjectFormModal, type ProjectFormValues } from '../components/ProjectFormModal';
import { EmptyBlock, ErrorBlock } from '../components/StateBlocks';
import { ProjectIcon } from '../components/ProjectIcon';
import { ProjectGlyph, resolveProjectColor } from '../components/ProjectIdentity';
import { PersonAvatarStack } from '../components/PersonAvatar';
import { emitJourneyEvent, JOURNEY_EVENTS } from '@modules/journey/detect';
import './ProjectsListPage.css';



function formatWhen(iso: string | null | undefined, t: (k: string) => string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) return t('time.today') || 'Today';
  if (days === 1) return t('time.yesterday') || 'Yesterday';
  if (days < 30) return (t('time.daysAgo') || '{{n}}d ago').replace('{{n}}', String(days));
  return new Date(iso).toLocaleDateString(activeLocale());
}

export const ProjectsListPage: React.FC = () => {
  const { t, tRef } = useTranslator();
  const { user } = useAuth();
  const navigate = useNavigate();
  const tenantPath = useTenantPath();

  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [query, setQuery] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    projectService
      // Closed projects come back with the rest and are marked on the card —
      // a filter chip for something this rare was noise beside the search.
      .listProjects(true)
      .then(setProjects)
      .catch(() => setError(
        tRef.current('projects.list.loadFailed') || t('projects.list.loadFailed'),
      ))
      .finally(() => setLoading(false));
    // `t` out of the deps on purpose — see useTranslator.ts.
  }, [tRef]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (values: ProjectFormValues) => {
    const project = await projectService.createProject(values);
    // Journey detection: their first shared workspace exists.
    emitJourneyEvent(JOURNEY_EVENTS.projectCreated);
    // Straight into the new project: the next thing anyone does is add context,
    // files or people, and all three live inside it.
    navigate(tenantPath(`/projects/${project.project_id}`));
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(p =>
      p.project_name.toLowerCase().includes(q)
      || (p.project_description || '').toLowerCase().includes(q)
      || (p.created_by_name || '').toLowerCase().includes(q));
  }, [projects, query]);

  const mine = filtered.filter(p => p.created_by_user_id === user?.user_id);
  const shared = filtered.filter(p => p.created_by_user_id !== user?.user_id);

  const renderCard = (project: ProjectSummary) => {
    // The card's whole identity — the glyph tile, the top edge, the hover
    // border — is driven from one custom property, so a creator's colour
    // choice lands everywhere at once and nothing hardcodes a hue.
    const accent = resolveProjectColor(project.project_color);
    const people = (project.recent_contributors || []).map(c => ({
      id: c.user_id, name: c.name, email: c.email,
    }));

    return (
      <button
        key={project.project_id}
        type="button"
        className={`pj-card pj-card-interactive project-card ${project.is_archived ? 'is-archived' : ''}`}
        style={{ ['--project-accent' as string]: accent }}
        onClick={() => navigate(tenantPath(`/projects/${project.project_id}`))}
      >
        <span className="project-card-top">
          <span className="project-card-icon" aria-hidden="true">
            <ProjectGlyph icon={project.project_icon} strokeWidth={1.7} />
          </span>
          <span className="project-card-headings">
            <span className="project-card-name" dir="auto">{project.project_name}</span>
            <span dir="auto" className="project-card-owner">
              {project.is_creator
                ? (t('projects.list.yoursLabel') || 'Yours')
                : (project.created_by_name || project.owner_tenant_name || '')}
            </span>
          </span>
          {project.is_archived && (
            <span className="pj-pill pj-pill-neutral">
              {t('projects.list.closed') || 'Closed'}
            </span>
          )}
        </span>

        <span dir="auto" className="project-card-desc bidi-clamp">
          {project.project_description
            || (t('projects.list.noDescription') || 'No description yet.')}
        </span>

        {/* Faces in the corner: who is in here is the fastest way to
            recognise which project this is. */}
        <span className="project-card-foot">
          <span className="project-card-people">
            {people.length > 0
              ? <PersonAvatarStack people={people} max={4} size={24} />
              : (
                <span className="project-card-meta-item">
                  {(() => {
                    const total = project.member_count + 1;
                    const copy = total === 1
                      ? (t('projects.list.memberCountOne') || '{{n}} member')
                      : (t('projects.list.memberCount') || '{{n}} members');
                    return copy.replace('{{n}}', String(total));
                  })()}
                </span>
              )}
          </span>
          <span className="project-card-when">
            {formatWhen(project.updated_at || project.created_at, t)}
          </span>
        </span>
      </button>
    );
  };

  const body = (() => {
    if (loading) {
      // The placeholder has to occupy the SAME geometry as the answer, or the
      // page rearranges itself under the reader: a flat list of bars sat where
      // the section heading would later appear, so every card jumped downward
      // and changed shape the moment the data landed. Same section heading,
      // same grid, same card height — only the contents are grey.
      return (
        <section className="project-section" aria-busy="true">
          {/* A real heading element, so its line box is byte-identical to
              the loaded one — a bar with its own height was 5px short and
              still nudged every card. */}
          <h2 className="project-section-title">
            <span className="project-skeleton-title" />
          </h2>
          <div className="project-grid">
            {Array.from({ length: 3 }).map((_, index) => (
              <div className="pj-card project-card project-card-skeleton" key={index}>
                <span className="project-card-top">
                  <span className="project-skeleton-tile" />
                  <span className="project-skeleton-headings">
                    <span className="project-skeleton-line is-strong" />
                    <span className="project-skeleton-line is-short" />
                  </span>
                </span>
                <span className="project-skeleton-line" />
                <span className="project-skeleton-line is-short" />
                <span className="project-skeleton-foot">
                  <span className="project-skeleton-dots" />
                  <span className="project-skeleton-line is-tiny" />
                </span>
              </div>
            ))}
          </div>
        </section>
      );
    }
    if (error) return <ErrorBlock message={error} onRetry={load} />;

    if (projects.length === 0) {
      return (
        <EmptyBlock
          icon={<ProjectIcon strokeWidth={1.6} />}
          title={t('projects.list.emptyTitle') || 'No projects yet'}
          body={t('projects.list.emptyBody')
            || 'A project is a shared space with its own brief and files. Create one, invite the people working on it, and every conversation inside stays open to the whole group.'}
          action={(
            <button type="button" className="pj-btn pj-btn-primary" onClick={() => setCreateOpen(true)}>
              {t('projects.list.create') || 'New project'}
            </button>
          )}
        />
      );
    }

    if (filtered.length === 0) {
      return (
        <EmptyBlock
          icon={<ProjectIcon strokeWidth={1.6} />}
          title={t('projects.list.noMatchesTitle') || 'No projects match'}
          body={t('projects.list.noMatchesBody') || 'Try a different search term.'}
          action={(
            <button type="button" className="pj-btn pj-btn-secondary" onClick={() => setQuery('')}>
              {t('projects.list.clearSearch') || 'Clear search'}
            </button>
          )}
        />
      );
    }

    return (
      <>
        {mine.length > 0 && (
          <section className="project-section">
            <h2 className="project-section-title">{t('projects.list.yours') || 'Your projects'}</h2>
            <div className="project-grid">{mine.map(renderCard)}</div>
          </section>
        )}
        {shared.length > 0 && (
          <section className="project-section">
            <h2 className="project-section-title">
              {t('projects.list.sharedWithYou') || 'Shared with you'}
            </h2>
            <div className="project-grid">{shared.map(renderCard)}</div>
          </section>
        )}
      </>
    );
  })();

  return (
    <div className="admin-container">
      <Sidebar />
      <div className="main-container">
        <main className="main-content projects-page">
          {/* The title row carries no action: the notification bell is fixed in
              that corner on every page, and a primary button there sits under
              it. New project lives on the controls row instead, level with the
              search field. */}
          <header className="projects-header">
            <div className="projects-header-text">
              <h1 className="projects-title">{t('projects.title') || 'Projects'}</h1>
              <p className="projects-subtitle">
                {t('projects.subtitle')
                  || 'Shared workspaces where a group works one problem together.'}
              </p>
            </div>
          </header>

          <div className="projects-controls">
            <div className="share-search projects-search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                dir="auto"
                className="share-search-input"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('projects.list.searchPlaceholder') || 'Search projects…'}
                aria-label={t('projects.list.searchPlaceholder') || 'Search projects'}
              />
            </div>
            <button
              type="button"
              className="pj-btn pj-btn-primary projects-create"
              data-testid="projects-create-button"
              onClick={() => setCreateOpen(true)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              {t('projects.list.create') || 'New project'}
            </button>
          </div>

          {body}
        </main>
      </div>

      <ProjectFormModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />
    </div>
  );
};

export default ProjectsListPage;
